import React, { useEffect, useState } from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { Typography, Container, Button } from "@mui/material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@fontsource/montserrat/500.css";
import "@fontsource/poppins/700.css";
import SignIn from "@/components/auth/SignIn";
import useAuth from "@/hooks/useAuth";
import { useSnackbar } from "@/contexts/SnackbarContext";

// Import images properly
import wyndhamLogo from "@/layouts/assets/images/Wyndham_new.svg";
import ihgLogo from "@/layouts/assets/images/ihg.png";
import hyattLogo from "@/layouts/assets/images/Hyatt.svg";
import marriottLogo from "@/layouts/assets/images/Marriot_new.svg";
import choiceLogo from "@/layouts/assets/images/choice.svg";
import g6Logo from "@/layouts/assets/images/G6.svg";
import logo3 from "@/layouts/assets/logo4.svg";
import dashboardImage from "@/layouts/assets/dashboard3.png";

const hotels = [
  { name: "Wyndham", logo: wyndhamLogo },
  { name: "IHG", logo: ihgLogo },
  { name: "Hyatt", logo: hyattLogo },
  { name: "Marriott", logo: marriottLogo },
  { name: "Choice", logo: choiceLogo },
  { name: "G6 Hospitality", logo: g6Logo },
];

function SignInPage() {
  const [sliderLoaded, setSliderLoaded] = useState(false);
  const { signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { showSnackbar } = useSnackbar();
  const imgSrc = dashboardImage;
  const [reloadKey, setReloadKey] = useState(0);

  // Reload the image when back online
  useEffect(() => {
    const handleOnline = () => {
      setReloadKey((prev) => prev + 1);
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []);

  const handleSignIn = async () => {
    setIsLoading(true);
    try {
      await signIn();
    } catch (error: any) {
      showSnackbar("Sign-In Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Ensure slider loads properly after component mounts
    const timer = setTimeout(() => {
      setSliderLoaded(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const settings = {
    infinite: true,
    speed: 1000,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 5,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
        },
      },
    ],
  };

  return (
    <Box
      sx={{
        background: "conic-gradient(from 135deg, #ffffff, #f0faff)",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "#f5f6fa",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.3)",
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 2, sm: 3, md: 6 },
            py: 0.5,
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={logo3}
              alt="Atica Logo"
              style={{
                height: 40,
                width: "auto",
              }}
            />
          </Box>

          {/* Sign In Button */}
          <SignIn />
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          pt: { xs: 12, sm: 14, md: 16 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: "center",
            gap: { xs: 4, lg: 6 },
            mb: { xs: 6, md: 8 },
            mt: 2,
          }}
        >
          {/* Left Side - Text Content */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: "center", lg: "left" },
              mt: { xs: 4, md: 6, lg: 15 },
            }}
          >
            <Box sx={{ mb: 6 }}>
              <img
                src={logo3}
                alt="Atica"
                style={{ width: "100%", maxWidth: 320, height: "auto" }}
              />
            </Box>

            <Typography
              variant="h2"
              sx={{
                color: "hsl(215, 16%, 47%)",
                fontWeight: 500,
                fontSize: { xs: "1rem", md: "1.1rem", lg: "1.8rem" },
                lineHeight: 1.4,
                maxWidth: { lg: "100%" },
                mb: 2,
              }}
            >
              Next-Generation Hospitality Management Platform by Atica
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleSignIn}
              disabled={isLoading}
              sx={{
                backgroundColor: "#1E4D91",
                color: "white",
                fontWeight: 600,
                fontSize: "1.1rem",
                px: 4,
                py: 1.5,
                my: 4,
                borderRadius: 50,
                textTransform: "none",
                boxShadow: "0 4px 14px 0 rgba(30, 77, 145, 0.3)",
                minWidth: 160,
                minHeight: 48,
                "&:hover": {
                  backgroundColor: "#1a4082",
                  boxShadow: "0 6px 20px 0 rgba(30, 77, 145, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </Box>

          {/* Right Side - Dashboard Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{ maxWidth: { xs: 320, sm: 380, lg: 800 }, width: "100%" }}
            >
              <img
                key={reloadKey} // This forces the <img> to reload
                src={imgSrc}
                alt="Dashboard Preview"
                onError={() => {}}
                style={{
                  width: "110%",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Hotel Brands Section - Full Width */}
      <Box
        sx={{
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 4 },
          background: "linear-gradient(135deg, #f8fafc, #f0f9ff, #f8fafc)",
          mb: 3,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h6"
            sx={{
              color: "#1E4D91",
              textAlign: { xs: "center", lg: "left" },
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: "1.2rem", md: "1.35rem" },
            }}
          >
            Trusted by hundreds of hoteliers for the brands
          </Typography>
        </Container>

        {/* Hotel Logos Carousel - Full Width */}
        <Box
          sx={{
            width: "100vw",
            position: "relative",
            left: "50%",
            right: "50%",
            marginLeft: "-50vw",
            marginRight: "-50vw",
            px: { xs: 2, sm: 4, md: 6 },

            "& .slick-track": {
              display: "flex",
              alignItems: "center",
            },
            "& .slick-slide": {
              padding: { xs: "0 15px", sm: "0 25px", md: "0 30px" },
            },
            "& .slick-slide > div": {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: { xs: "60px", sm: "75px", md: "80px" },
            },
          }}
        >
          {sliderLoaded && (
            <Slider {...settings}>
              {hotels.map((hotel, index) => (
                <Box key={`${hotel.name}-${index}-${reloadKey}`}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <img
                      src={hotel.logo}
                      alt={`${hotel.name} logo`}
                      key={reloadKey}
                      style={{
                        maxWidth: "160px",
                        maxHeight: "50px",
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        filter: "grayscale(80%) opacity(0.8)",
                        transition: "filter 0.3s ease, transform 0.2s ease",
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.filter =
                          "grayscale(0%) opacity(1)";
                        e.currentTarget.style.transform = "scale(1.05)";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.filter =
                          "grayscale(80%) opacity(0.8)";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Slider>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Container maxWidth="lg">
        <Box
          component="footer"
          sx={{
            py: 2,
            px: { xs: 2, sm: 3 },
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.8rem",
            }}
          >
            Copyrights © Atica Global Inc / Atica Hospitality Pvt Ltd
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default SignInPage;
